import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Share2, Copy, Check, Code, Image, FileText } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmbedStatCard = ({ isDark }) => {
  const { user, token } = useAuth();
  const [snippets, setSnippets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (user?.username) fetchSnippets();
  }, [user?.username]);

  const fetchSnippets = async () => {
    try {
      const res = await axios.get(`${API}/embed/snippet/${user.username}`);
      setSnippets(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const copyToClipboard = async (text, label) => {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    toast({ title: 'Copied!', description: `${label} embed code copied to clipboard` });
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) return <div className="flex justify-center h-32 items-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>;
  if (!snippets) return null;

  const embedOptions = [
    { key: 'html_iframe', label: 'HTML Iframe', icon: Code, desc: 'Embed on any website' },
    { key: 'svg_image', label: 'Image Tag', icon: Image, desc: 'For blogs & websites' },
    { key: 'markdown', label: 'Markdown', icon: FileText, desc: 'For GitHub & Reddit' },
    { key: 'bbcode', label: 'BBCode', icon: Code, desc: 'For forums' },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="embed-stat-card">
      <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
        <Share2 className="h-5 w-5 text-blue-600" /> Share Your Stats
      </h2>
      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        Embed your stat card on forums, blogs, and social media. Every embed links back to your profile.
      </p>

      {/* Preview */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
        <CardContent className="p-4">
          <p className={`text-xs font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Preview</p>
          <div className="flex justify-center">
            <img
              src={`${API}/embed/card/${user?.username}/svg`}
              alt={`${user?.username} stat card`}
              className="max-w-full rounded-lg"
              data-testid="embed-preview-svg"
            />
          </div>
        </CardContent>
      </Card>

      {/* Embed Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {embedOptions.map(opt => {
          const Icon = opt.icon;
          const code = snippets.embed_codes[opt.key];
          const isCopied = copied === opt.label;
          return (
            <Card key={opt.key} className={`${isDark ? 'bg-slate-800 border-slate-700' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className={`text-sm font-semibold ${isDark ? 'text-white' : ''}`}>{opt.label}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(code, opt.label)}
                    className="h-7"
                    data-testid={`copy-${opt.key}`}
                  >
                    {isCopied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
                <p className={`text-xs mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{opt.desc}</p>
                <pre className={`text-[10px] p-2 rounded-lg overflow-x-auto whitespace-pre-wrap break-all ${isDark ? 'bg-slate-900 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                  {code}
                </pre>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Direct link */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-semibold ${isDark ? 'text-white' : ''}`}>Direct SVG Link</p>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{snippets.embed_codes.direct_link}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(snippets.embed_codes.direct_link, 'Direct Link')}
              data-testid="copy-direct-link"
            >
              {copied === 'Direct Link' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmbedStatCard;
